import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, AlertTriangle } from "lucide-react";

export default function Budgets() {
  const { companyId, cur } = useCompany();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", scope: "month", period: new Date().toISOString().slice(0, 7), planned: 0 });

  const load = () => api.get("/budgets", { params: { company_id: companyId } }).then(({ data }) => setItems(data.budgets));
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const save = async () => {
    try { await api.post("/budgets", { ...form, planned: parseFloat(form.planned) }, { params: { company_id: companyId } });
      toast.success("Budget créé"); setOpen(false); setForm({ name: "", scope: "month", period: new Date().toISOString().slice(0, 7), planned: 0 }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="budgets-page">
      <PageHeader title="Budgets & alertes" subtitle="Suivi budget vs dépensé avec alertes de dépassement." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-budget-button"><Plus className="w-4 h-4 mr-1" />Nouveau budget</Button></DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle className="font-display">Nouveau budget</DialogTitle><DialogDescription>Définissez un budget mensuel et son montant prévu.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Budget marketing" data-testid="budget-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Période (AAAA-MM)</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="budget-period-input" /></div>
                <div className="space-y-1"><Label>Montant prévu</Label><Input type="number" value={form.planned} onChange={(e) => setForm({ ...form, planned: e.target.value })} data-testid="budget-planned-input" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={!form.name} className="rounded-full" data-testid="save-budget-button">Enregistrer</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />
      {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : items.length === 0 ? <EmptyState title="Aucun budget" hint="Créez un budget mensuel." />
        : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((b) => (
              <Card key={b.id} className={`p-5 rounded-md shadow-sm ${b.over ? "border-rose-300" : "border-border"}`} data-testid={`budget-${b.id}`}>
                <div className="flex items-center justify-between"><div className="font-display font-semibold">{b.name}</div>{b.over && <AlertTriangle className="w-4 h-4 text-rose-500" data-testid={`budget-alert-${b.id}`} />}</div>
                <div className="text-xs text-muted-foreground">{b.period}</div>
                <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden"><div className={`h-full ${b.over ? "bg-rose-500" : "bg-primary"}`} style={{ width: `${Math.min(b.percent, 100)}%` }} /></div>
                <div className="flex justify-between text-sm mt-2"><span className="text-muted-foreground">Dépensé</span><Money value={b.spent} cur={cur} /></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Prévu</span><Money value={b.planned} cur={cur} /></div>
                <div className={`flex justify-between text-sm font-semibold ${b.over ? "text-rose-600" : "text-emerald-600"}`}><span>Disponible</span><Money value={b.available} cur={cur} /></div>
              </Card>))}
          </div>}
    </div>
  );
}
